import React, {useState, useMemo, useEffect} from "react";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import {FormattedMessage, injectIntl} from "react-intl";
import Select, {SelectOption} from "@amzn/meridian/select";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import Tag from "@amzn/meridian/tag";
import axios from "axios";
import {ALERT_TYPES, EDIT_PROGRAM_ACTIONS} from "../../Constants";
import {getErrorAlertObjectFromResponse, getHeadersForNFMS, getNFMSEndpoint} from "../../Utils";
import {fetchAllPrograms, fetchFeatures} from "../../NetworkUtils";
import Alert from "@amzn/meridian/alert";
import {getUserLogin} from "../permissions/PermissionContext";


const EditProgram = () => {
    const [programName, setProgramName] = useState();
    const [teamName, setTeamName] = useState();
    const [action, setAction] = useState(EDIT_PROGRAM_ACTIONS.CREATE_PROGRAM);
    const [mcmId, setMcmId] = useState();
    const [programList, setProgramList] = useState([]);
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [featureSearchQuery, setFeatureSearchQuery] = useState();
    const [allFeatures, setAllFeatures] = useState([]);
    const [alert, setAlert] = useState({visible: false, text:"", type:""})
    const warningAlert = {visible: true, text: "Edit Program request received.", type:ALERT_TYPES.WARNING};
    const successAlert = {visible: true, text: "Successfully edited Program", type:ALERT_TYPES.SUCCESS};

    useEffect(() => {
        updateFeatureList();
    }, []);

    useEffect(() => {
        clearData();
        fetchAllPrograms().then(response => setProgramList(response));
        if (action.name !== EDIT_PROGRAM_ACTIONS.REMOVE_FEATURES.name) {
            updateFeatureList();
        }
    }, [action]);

    useEffect(() => {
        if (action.name !== EDIT_PROGRAM_ACTIONS.CREATE_PROGRAM.name) {
            updateFeatureList();
        }
    }, [programName]);

    const searchRegExp = useMemo(
        () => new RegExp(featureSearchQuery, "i"),
        [featureSearchQuery]
    )

    const matchedOptions = allFeatures.filter(
        feature => !featureSearchQuery || searchRegExp.test(feature)
    )

    const updateFeatureList = () => {
        fetchFeatures(
            action.name === EDIT_PROGRAM_ACTIONS.REMOVE_FEATURES.name ? programName : null
        ).then(response => {
            setAllFeatures(response);
        });
    }

    const deselectFeature = (featureToBeRemoved) => {
        let featuresCopy = selectedFeatures.filter(featureId => {return featureId !== featureToBeRemoved;})
        setSelectedFeatures(featuresCopy);
    }


    const clearData = () => {
        setProgramName(null);
        setAllFeatures([]);
        setMcmId(null);
        setSelectedFeatures([]);
    }

    const editProgram = () => {
        setAlert(warningAlert);
        (async ()=>{
            const headers = await getHeadersForNFMS("EditProgram");
            const userAlias = await getUserLogin();
            const params = { program: programName, features: selectedFeatures, action: action.name, mcmId: mcmId, userAlias, teamName};
            axios
                .post(getNFMSEndpoint(), params, headers)
                .then((response) => {
                    if (response.status === 200) {
                        setAlert(successAlert);
                    }
                })
                .catch((response) => {
                    setAlert(getErrorAlertObjectFromResponse(response));
                })
        })();
    }

    return (
        <div className="App">

            <Row width="100%" spacingInset="small small medium none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="editProgram.option" defaultMessage="Option"/>
                    </Text>
                    <Select
                        value={action.name}
                        width={400}
                        size={"medium"}
                        onChange={(OPTION) => setAction(EDIT_PROGRAM_ACTIONS[OPTION])} >
                        {Object.entries(EDIT_PROGRAM_ACTIONS).map(([key, value]) => {
                            return <SelectOption label={value.description} value={value.name} key={value.name} />
                        })}
                    </Select>
                </Column>
            </Row>

            { (action.name === EDIT_PROGRAM_ACTIONS.ADD_FEATURES.name || action.name === EDIT_PROGRAM_ACTIONS.REMOVE_FEATURES.name) ?
                <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none small medium none">
                    <Column spacing="200" alignmentHorizontal="left">
                        <Text type="b200" color="secondary">
                            <FormattedMessage id="editProgram.programName" defaultMessage="Program Name"/>
                        </Text>
                        <Select value={programName} onChange={setProgramName} placeholder="Select Existing Program"
                                width={400}>
                            {programList.map(program => (
                                <SelectOption label={program} value={program} key={program}/>
                            ))}
                        </Select>
                    </Column>
                </Row>
            : null}

            { action.name === EDIT_PROGRAM_ACTIONS.CREATE_PROGRAM.name ?
                <div>
                    <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none small medium none">
                        <Column spacing="200" alignmentHorizontal="left">
                            <Text type="b200" color="secondary">
                                <FormattedMessage id="editProgram.newProgram" defaultMessage="New Program Name"/>
                            </Text>
                            <Input id="program-name" value={programName} width={400}
                                   onChange={setProgramName} type="text" placeholder="Enter new Program name"/>
                        </Column>
                    </Row>
                    <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none small medium none">
                        <Column spacing="200" alignmentHorizontal="left">
                            <Text type="b200" color="secondary">
                                <FormattedMessage id="editProgram.teamName" defaultMessage="Team Name"/>
                            </Text>
                            <Input id="team-name" value={teamName} width={400}
                                   onChange={setTeamName} type="text" placeholder="Enter Team Name for permissions"/>
                        </Column>
                    </Row>
                </div>
            : null}

            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="editProgram.mcm" defaultMessage="MCM Id"/>
                    </Text>
                    <Input id="mcm-input" value={mcmId} onChange={setMcmId} type="text" placeholder="Enter MCM Id" width={400}/>
                </Column>
            </Row>

            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="editProgram.features" defaultMessage="Features"/>
                    </Text>
                    <Select searchQuery={featureSearchQuery} onSearch={setFeatureSearchQuery} value={selectedFeatures}
                            onChange={setSelectedFeatures} placeholder="Select Features" width={400}>
                        {matchedOptions.map(feature => (
                            <SelectOption label={feature} value={feature} key={feature} />
                        ))}
                    </Select>
                </Column>
            </Row>

            <Row alignmentVertical="top" spacingInset="none none medium none" wrap="down">
                {selectedFeatures.map(feature => (
                    <Tag type="neutral" key={feature} onClose={() => deselectFeature(feature)}>
                        {feature}
                    </Tag>
                ))}
            </Row>

            <Row widths={["grid-2", "grid-2"]} alignmentVertical="top" spacingInset="large large large none">
                <Column spacing="200" alignmentHorizontal="left">
                    { action.name === EDIT_PROGRAM_ACTIONS.REMOVE_FEATURES.name ?
                        <Button onClick={editProgram}>
                            <FormattedMessage id="editProgram.removeFeatures" defaultMessage="Remove Features"/>
                        </Button>
                        :
                        <Button onClick={editProgram}>
                            <FormattedMessage id="editProgram.addFeatures" defaultMessage="Add Features"/>
                        </Button>
                    }
                </Column>
            </Row>
            <Row alignmentVertical="top" spacingInset="none none medium none">
                {alert.visible ? <Alert type={alert.type}>{alert.text}</Alert> : null}
            </Row>
        </div>
    );
}

export default injectIntl(EditProgram);


// WEBPACK FOOTER //
// ./src/components/edit-program/EditProgram.js